import React, { useMemo, useState } from 'react'
import useNavigation from 'core/hooks/useNavigation'
import Popover from 'core/components/dialogs/Popover'
import { useUserProfile } from 'core/hooks/useDataHook'
import { UserProfile } from 'types/entity'
import useRolesAndPermissions from 'core/hooks/useRolesAndPermissions'
import cn from 'classnames'
import Icon from 'core/components/icons/Icon'

const NavPopover = () => {
  const { push, pathname } = useNavigation()
  const [show, setShow] = useState(false)
  const isInTeamsPath =
    pathname?.toLocaleLowerCase().includes('team') &&
    pathname !== '/account/team'
  const isShared = pathname?.toLocaleLowerCase().includes('shared')
  const isOnboarding =
    pathname?.toLocaleLowerCase().includes('step') ||
    pathname?.toLocaleLowerCase().includes('about-you')

  const {
    permissions: { hasAccessToTeamDashboard },
    hasOnlyRole
  } = useRolesAndPermissions()
  const isOnlyReviewer = hasOnlyRole('isReviewer')
  const { data: currentUser, isLoading } = useUserProfile({
    opts: {
      placeholderData: {} as UserProfile
    }
  })

  const { email, teams } = currentUser || {}
  const { team_name } = teams?.[0] ?? {}
  const hasTeam = !!teams?.length
  const onTeamRouteOrShared = isInTeamsPath || (isShared && hasTeam)

  const title = onTeamRouteOrShared ? team_name || 'Team Dashboard' : email

  const isSafari = useMemo(() => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  }, [])

  const menu = useMemo(() => {
    return (
      <div
        className={cn([
          'bg-white shadow-lg rounded-md',
          isSafari ? 'mt-10 ml-52' : '-mt-3'
        ])}
      >
        <div
          className="hover:bg-secondary-1 px-3 py-3 cursor-pointer bg-transparent  transition-colors duration-300"
          onClick={() => {
            push('/dashboard')
          }}
        >
          <div className=" font-medium">{email}</div>
          <span className="text-dark-3  text-base">Personal Dashboard</span>
        </div>

        <div
          className="hover:bg-secondary-1 px-3 py-3 cursor-pointer bg-transparent transition-colors duration-300"
          onClick={() => {
            push('/teams')
          }}
        >
          <div className=" font-medium">{team_name ?? email}</div>
          <span className="text-dark-3  text-base">Team Dashboard</span>
        </div>
      </div>
    )
  }, [email, isSafari, push, team_name])

  const content = useMemo(() => {
    if (isLoading) {
      return <Icon name="loading" className="text-primary" />
    }

    if (onTeamRouteOrShared && isOnlyReviewer) {
      return (
        <span className="text-xl font-medium cursor-default">
          Team Dashboard
        </span>
      )
    }

    if (isOnlyReviewer) {
      return <span className="text-xl font-medium cursor-default">{email}</span>
    }

    if (isOnboarding) {
      return `Welcome, ${email ?? ''}`
    }

    if (onTeamRouteOrShared && hasTeam) {
      return (
        <div className="flex items-center gap-3 select-none">
          <span>
            <div className="text-lg">{team_name || email}</div>
            <span className="text-dark-3 ">Team Dashboard</span>
          </span>
          <Icon name="down" size={14} />
        </div>
      )
    }

    if (!onTeamRouteOrShared && hasTeam) {
      return (
        <div className="flex items-center gap-3 select-none">
          <span>
            <div className="text-lg">{email}</div>
            <span className="text-dark-3 ">Personal Dashboard</span>
          </span>
          {hasAccessToTeamDashboard && <Icon name="down" size={14} />}
        </div>
      )
    }

    return (
      <h1 style={{ fontSize: 16 }} className="normal-case m-0 hidden md:block">
        {title}
      </h1>
    )
  }, [
    email,
    hasAccessToTeamDashboard,
    hasTeam,
    isLoading,
    isOnboarding,
    isOnlyReviewer,
    onTeamRouteOrShared,
    team_name,
    title
  ])

  return (
    <div className="z-20">
      <Popover
        content={
          hasAccessToTeamDashboard && !isLoading && !isOnlyReviewer
            ? menu
            : null
        }
        show={show}
        position="bottom-middle"
        useArrow={false}
      >
        <div
          onClick={() => setShow(!show)}
          onBlur={() => setShow(false)}
          tabIndex={0}
          className="cursor-pointer select-none"
        >
          {content}
        </div>
      </Popover>
    </div>
  )
}

export default NavPopover
